import { Table } from '@chakra-ui/react';

import {
  IProduct,
  IQuoteItemWithDisplayType,
  IRateReqSchemaUI,
  PriceDisplayEnum,
  ProductTypeEnum,
  RateBillingFrequencyEnum,
  RateUsageBillingFrequencyEnum,
} from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { FC } from 'react';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../constants/products';
import { getPricePerUnitWithRateFrequencyShortCode } from '../../utils/quotes';
import { MBox, MTable, MText } from '../Monetize';

interface RateSettingsPopoverTableProps {
  quoteOfferingName: string;
  products: Pick<IProduct, 'productType' | 'name' | 'id'>[];
  items?: IQuoteItemWithDisplayType[];
  rate: IRateReqSchemaUI;
  priceDisplay: PriceDisplayEnum;
}

export const RateSettingsPopoverTable: FC<RateSettingsPopoverTableProps> = ({
  products,
  items,
  rate,
  priceDisplay,
  quoteOfferingName,
}: RateSettingsPopoverTableProps) => {
  const dateStr = `${toDateShort(new Date(), 'userTimezone')} - ${toDateShort(
    addDays(addMonths(new Date(), 12), -1),
    'userTimezone',
  )}`;

  const isProductLevel = priceDisplay === PriceDisplayEnum.PRODUCT;
  if (!rate) {
    return null;
  }

  const prices = products.slice(0, 2).map((product) => {
    const productPrices =
      product.id && rate.products?.[product.id]?.prices
        ? rate.products?.[product.id]?.prices
        : [];
    const price = productPrices.length > 0 ? productPrices[0].amount : 0;

    return {
      name: product.name,
      quantity: items
        ? items.find((item) => item.productId === product.id)?.quantity
        : 1,
      productTypeDesc: PRODUCT_TYPE_DISPLAY_SHORT[product.productType],
      ppu: getPricePerUnitWithRateFrequencyShortCode({
        priceStr: formatCurrency(price, { currency: rate.currency }),
        offeringRate: {
          ...rate,
          billingFrequency:
            rate.billingFrequency || RateBillingFrequencyEnum.MONTHLY,
          usageBillingFrequency:
            rate.usageBillingFrequency || RateUsageBillingFrequencyEnum.MONTHLY,
          //  'currency'
          prices: rate.prices || ([] as any[]),
        },
        productType: product.productType,
        item: { productId: product.id ?? '' },
        isCustomPrice: false,
      }),
      amountStr:
        product.productType !== ProductTypeEnum.USAGE
          ? formatCurrency(price, { currency: rate.currency })
          : '',
      amount: product.productType !== ProductTypeEnum.USAGE ? price : 0,
      description: rate.description,
    };
  });
  const total = prices.reduce((prev, curr) => (+curr.amount || 0) + prev, 0);
  const totalStr = formatCurrency(total, { currency: rate.currency });

  return (
    <MTable mt={2} variant="preview">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>Offering</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">Date</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center" minW="50px">
            Quantity
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center" minW="50px">
            PPU
          </Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right" minW="50px">
            Amount
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <Table.Row background="tBlue.hover">
          <Table.Cell maxW="120px">
            <MText truncate fontSize="9px" fontWeight="bold">
              {quoteOfferingName}
            </MText>
          </Table.Cell>
          <Table.Cell textAlign="center" fontSize="9px" minW="120px">
            {dateStr}
          </Table.Cell>
          <Table.Cell textAlign="center"></Table.Cell>
          <Table.Cell textAlign="center"></Table.Cell>
          <Table.Cell textAlign="right"></Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell fontWeight="bold">{rate?.name}</Table.Cell>
          <Table.Cell textAlign="center">
            <MBox fontSize="9px">
              {dateStr}
              <MText fontSize="9px" lineHeight="1">
                12 Months
              </MText>
            </MBox>
          </Table.Cell>
          <Table.Cell textAlign="center"></Table.Cell>
          <Table.Cell textAlign="center"></Table.Cell>
          <Table.Cell textAlign="right">
            {isProductLevel ? '' : totalStr}
          </Table.Cell>
        </Table.Row>
        {prices.map(
          (
            { name, quantity, ppu, productTypeDesc, amountStr, description },
            index,
          ) => (
            <Table.Row key={index}>
              <Table.Cell maxW="120px" paddingLeft="10px !important">
                <MText fontSize="8px" lineHeight="0.8rem" lineClamp={1}>
                  <b>{name}</b> {productTypeDesc ? `(${productTypeDesc})` : ''}
                </MText>
                <MText fontSize="8px" lineHeight="0.8rem" lineClamp={1}>
                  {description}
                </MText>
              </Table.Cell>
              <Table.Cell textAlign="center"></Table.Cell>
              <Table.Cell textAlign="center">{quantity}</Table.Cell>
              <Table.Cell textAlign="center">
                {isProductLevel ? ppu : ''}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {isProductLevel ? amountStr : ''}
              </Table.Cell>
            </Table.Row>
          ),
        )}
      </Table.Body>
    </MTable>
  );
};
